import React, { FC } from 'react';
import s from './paymentHistoryItem.module.scss';
import { purchase } from '@prisma/client';
import cn from 'classnames';
import { PaymentHistoryImage } from '@components/Cards/PaymentHistoryItem/PaymentHistoryImage';
import { useSession } from 'next-auth/react';
import { ChikoSession, PurchaseTypes } from '@specs/specs';
import { formatRubyAmount, formatWithSpaces } from '@specs/helpers';
import { useGetToy } from '@hooks/TRPC_Hooks/useGetToy';
import { getText, getTitle } from '@services/payments/helpers/history';
import { useTranslation } from '@hooks/useTranslation';
import { SpriteIcon } from '@components/Sprites/SpriteIcon';
import { dropsKeys } from '@lang/en/drops';
import { useGetSingleBlindBox } from '@services/blindBox/hooks/getSingle';
import { useGetSinglePack } from '@services/partnerDrop/hooks/getSingle';
import { useGetSingleSubscription } from '@services/subscription/hooks/getSingleSubscription';
import { referralAwardsKeys } from '@lang/en/referralAwards';
import { useGetRack } from '@services/social/hooks/useGetRack';
import { PaymentHistoryItemLoader } from '@components/Cards/PaymentHistoryItem/PaymentHistoryItemLoader';
import { useGetPublicProfile } from '@hooks/TRPC_Hooks/useGetPublicProfile';
interface Props extends purchase {}
export const PaymentHistoryItem: FC<Props> = ({
  type,
  currency,
  sellerId,
  price,
  toyId,
  dropMechanic,
  blindBoxId,
  packId,
  subscriptionId,
  award,
  rackId,
  referralShortId,
  provider
}) => {
  const {
    data
  } = useSession();
  const {
    translate: tr,
    intl
  } = useTranslation();
  const {
    data: toy,
    isLoading: toyLoading
  } = useGetToy({
    id: toyId ?? -1
  }, {
    enabled: typeof toyId === 'number'
  });
  const {
    data: blindBox,
    isLoading: blindBoxLoading
  } = useGetSingleBlindBox({
    id: blindBoxId ?? -1
  }, {
    enabled: typeof blindBoxId === 'number'
  });
  const {
    data: pack,
    isLoading: packLoading
  } = useGetSinglePack({
    id: packId ?? -1
  }, {
    enabled: typeof packId === 'number'
  });
  const {
    data: subscription,
    isLoading: subscriptionLoading
  } = useGetSingleSubscription({
    id: subscriptionId ?? -1
  }, {
    enabled: typeof subscriptionId === 'number'
  });
  const {
    data: rack,
    isLoading: rackLoading
  } = useGetRack({
    id: rackId ?? -1
  }, {
    enabled: typeof rackId === 'number'
  });
  const {
    data: referralProfile,
    isLoading: referralProfileLoading
  } = useGetPublicProfile({
    shortId: referralShortId ?? ''
  }, {
    enabled: typeof referralShortId === 'string'
  });
  const myProfileId = (data as ChikoSession)?.uid;
  const isMy = typeof sellerId === 'number' && (type === PurchaseTypes.GIFT_TOY || type === PurchaseTypes.PURCHASED_TOY) ? sellerId === myProfileId : true;
  const isPositive = type === PurchaseTypes.GIFT_TOY || type === PurchaseTypes.PURCHASED_TOY || type === PurchaseTypes.DIGITAL_DROP || type === PurchaseTypes.COMMISSION_ARTIST ? isMy : price >= 0;
  const absPrice = Math.abs(price);
  const isLoading = typeof toyId === 'number' && toyLoading || typeof blindBoxId === 'number' && blindBoxLoading || typeof packId === 'number' && packLoading || typeof subscriptionId === 'number' && subscriptionLoading || typeof rackId === 'number' && rackLoading || typeof referralShortId === 'string' && referralProfileLoading;
  if (type === 'CRON_COLLECT' || type === 'BLINDBOX_OPEN' || type === 'CUSTOM_BANNERS') return null;
  if (isLoading) return <PaymentHistoryItemLoader />;
  const text = getText(type, currency, isPositive, isMy);
  const title = getTitle(type, currency, isPositive, isMy);
  return <div className={s.paymentHistoryItem} data-sentry-component="PaymentHistoryItem" data-sentry-source-file="PaymentHistoryItem.tsx">
            <div className={s.paymentHistoryItem__body}>
                <div className={s.paymentHistoryItem__left}>
                    <PaymentHistoryImage type={type} currency={currency} isMy={isMy} isPositive={isPositive} toy={toy} referralProfile={referralProfile} provider={provider} data-sentry-element="PaymentHistoryImage" data-sentry-source-file="PaymentHistoryItem.tsx" />
                    <div className={s.paymentHistoryItem__info}>
                        <div className={cn(s.paymentHistoryItem__title, '_ellipsis')}>
                            {tr({
              id: title
            }, {
              dropType: tr({
                id: `drops.${dropMechanic}` as dropsKeys
              })
            })}
                        </div>
                        {typeof text !== 'undefined' && <div className={cn(s.paymentHistoryItem__text, '_ellipsis')}>
                                {tr({
              id: text
            }, {
              toyName: toy?.name,
              packName: typeof pack !== 'undefined' ? JSON.parse(pack.title as string)[intl.locale] : '',
              blindboxName: blindBox?.name,
              monthCount: typeof subscription !== 'undefined' ? subscription.plan === 'month' ? 1 : subscription.plan === 'sixMonths' ? 6 : subscription.plan === 'year' ? 12 : '' : '',
              referralAward: award ? tr({
                id: `award.${award}` as referralAwardsKeys
              }) : '',
              boardName: !!rack ? rack.name : tr({
                id: 'payment.history.page.text.board'
              })
            })}
                            </div>}
                    </div>
                </div>
                <div className={cn(s.paymentHistoryItem__amount, isPositive && s.paymentHistoryItem__amount_positive)}>
                    <span>
                        {isPositive ? '' : '-'} {formatWithSpaces(currency === 'RUBY' ? formatRubyAmount(absPrice) : absPrice)}
                    </span>
                    <SpriteIcon width={20} height={20} name={currency === 'RUBY' ? 'ruby' : 'gold'} data-sentry-element="SpriteIcon" data-sentry-source-file="PaymentHistoryItem.tsx" />
                </div>
            </div>
        </div>;
};